import { useEffect, useMemo } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { useAuthStore } from '@/features/auth/auth-store';
import { canActOnReferrals, isBranchScoped } from '@/features/auth/roles';
import { useCampaignCustomers } from '@/features/campaigns/useCampaignCustomers';
import { useProducts } from '@/features/product-scan/useProducts';
import { type Referral, useReferralsStore } from '@/features/referrals/referrals-store';
import { useReferralsSync } from '@/features/referrals/useReferralsSync';
import type { KpiDrillTarget } from '@/router/routes';

import { dashboardMetrics } from './dashboard-metrics';
import { InsightsByCategoryCard } from './InsightsByCategoryCard';
import { KpiRow } from './KpiRow';
import { ReferralsCard } from './ReferralsCard';
import { TellerPerformanceCard } from './TellerPerformanceCard';

export interface DashboardHomeProps {
  /** Navigate to the Referrals page, optionally pre-scoped to "assigned to me". */
  onOpenReferrals: (assignedToMe: boolean) => void;
  /** Start outreach for a referral: open Campaigns with customer + product staged. */
  onStartOutreach: (referral: Referral) => void;
  /**
   * SCRUM-847: drill a KPI card through to Campaigns pre-filtered to that card's
   * population (Potential Revenue → relgap, Deposits at Risk → flight, Customers
   * Reached → reached).
   */
  onOpenCampaignsFiltered: (target: KpiDrillTarget) => void;
}

/**
 * The Dashboard home page (SCRUM-324) — Ryan's v6 `renderDashboardPage`:
 * page header, the 4-up KPI row, the gold-tinted Referrals hero card, and the
 * static Insights-by-category card, stacked full-width in a 1180px column.
 *
 * Bank Executive view only in Phase 1 (role gating lands with SCRUM-103/290).
 * Referral data comes from the client-side store (SCRUM-325), seeded against
 * live customers/products; Phase 2a (SCRUM-343) swaps the store to the API.
 */
export function DashboardHome({
  onOpenReferrals,
  onStartOutreach,
  onOpenCampaignsFiltered,
}: DashboardHomeProps) {
  const { data: customers, isLoading: customersLoading } = useCampaignCustomers();
  const { data: products, isLoading: productsLoading } = useProducts();
  const seedFromLiveData = useReferralsStore((s) => s.seedFromLiveData);
  const referrals = useReferralsStore((s) => s.referrals);
  // v7 role scoping (SCRUM-388): BM/Teller compute from their branch only;
  // tellers get their own referral view + performance card and no insights.
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'Bank Executive';
  const canAct = canActOnReferrals(role);
  const isTeller = role === 'Teller';
  const branch = isBranchScoped(role) ? user?.branch : undefined;

  const safeCustomers = useMemo(() => customers ?? [], [customers]);
  const safeProducts = useMemo(() => products ?? [], [products]);

  // Seed the demo referrals once BOTH queries settle — success OR error
  // (idempotent). Gating on settle rather than on data presence matters: when
  // the API is unreachable (offline preview), data stays undefined forever,
  // and seeding must still run with empty datasets so the referrals card
  // falls back to the prototype display names. Caught by the local Playwright
  // validation pass (2026-07-02) — the data-presence gate left the card empty
  // offline while every unit test (mocked fetch) passed.
  useEffect(() => {
    if (!customersLoading && !productsLoading) {
      seedFromLiveData(safeCustomers, safeProducts);
    }
  }, [customersLoading, productsLoading, safeCustomers, safeProducts, seedFromLiveData]);

  // SCRUM-343: merge server referrals on top of the seeds once data settles.
  useReferralsSync(safeCustomers, !customersLoading && !productsLoading);

  const metrics = useMemo(
    () => dashboardMetrics(safeCustomers, referrals, branch),
    [safeCustomers, referrals, branch],
  );
  const scopedCustomers = useMemo(
    () => (branch === undefined ? safeCustomers : safeCustomers.filter((c) => c.branch === branch)),
    [safeCustomers, branch],
  );

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Dashboard"
        subtitle="Your latest activity, conversions, and referrals to action"
      />

      <div className="flex w-full max-w-[1180px] flex-col gap-6 p-8">
        <KpiRow metrics={metrics} onDrill={onOpenCampaignsFiltered} />
        <ReferralsCard onOpenReferrals={onOpenReferrals} onStartOutreach={onStartOutreach} />
        {isTeller && <TellerPerformanceCard />}
        {canAct && <InsightsByCategoryCard customers={scopedCustomers} branchLabel={branch} />}
      </div>
    </div>
  );
}
